import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { ChevronRight, Loader2, CheckCircle2, Lock } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { DISCOVERY_SKIPPED, DISCOVERY_SOURCES, type DiscoverySource } from "../lib/discoverySources";
import { ONBOARDING_ACKNOWLEDGEMENTS } from "../data/onboardingAcknowledgements";
import { OnboardingAcknowledgements } from "../components/OnboardingAcknowledgements";

export default function Welcome() {
  const [, navigate] = useLocation();
  const { user, loading } = useAuth();
  const [status, setStatus] = useState<Awaited<ReturnType<typeof api.onboardingStatus>> | null>(null);
  const [checks, setChecks] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  const [selectedSources, setSelectedSources] = useState<DiscoverySource[]>([]);
  const allChecked = ONBOARDING_ACKNOWLEDGEMENTS.every(({ key }) => checks[key]);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login", { replace: true }); return; }
    if (user.userType === "admin") { navigate("/admin", { replace: true }); return; }
    let active = true;
    setStatus(null);
    setErr(null);
    api.onboardingStatus().then((nextStatus) => {
      if (!active) return;
      if (!nextStatus.needsConsent && !nextStatus.discoveryPromptPending) navigate("/dashboard", { replace: true });
      else setStatus(nextStatus);
    }).catch(() => {
      if (active) setErr("Could not load your account setup. Please try again.");
    });
    return () => { active = false; };
  }, [loading, user, navigate, retry]);

  async function accept() {
    if (!allChecked || busy || !status?.needsConsent) return;
    setBusy(true); setErr(null);
    try {
      // [consent.recovery-discovery](../../../docs/product/accounts.md#consent-recovery-discovery): recover only missing acceptance. New signup accounts
      // bypass this prompt; discovery cannot grant consent or silently backfill it.
      await api.acceptConsent({ dataSharing: true, riskWaiver: true, accuracy: true });
      if (status.discoveryPromptPending) setStatus({ ...status, needsConsent: false });
      else navigate("/dashboard", { replace: true });
    } catch (error: unknown) {
      setErr(error instanceof Error ? error.message : "Could not save your acceptance. Please try again.");
    } finally { setBusy(false); }
  }

  async function finishDiscovery(skip: boolean) {
    if (busy || !status || status.needsConsent || (!skip && !selectedSources.length)) return;
    setBusy(true); setErr(null);
    try {
      await api.saveDiscovery(skip ? [DISCOVERY_SKIPPED] : selectedSources);
      navigate("/dashboard", { replace: true });
    } catch (error: unknown) {
      setErr(error instanceof Error ? error.message : "Could not save your answer. Please try again.");
    } finally { setBusy(false); }
  }

  return <div className="min-h-[80vh] grid place-items-center px-4 py-10">
    <div className="card w-full max-w-lg min-w-0 overflow-hidden p-6 sm:p-8">
      {!status ? <div className="text-center">
        {err ? <><p role="alert" className="text-sm text-brand-red">{err}</p><button type="button" onClick={() => setRetry((attempt) => attempt + 1)} className="btn-outline mt-4">Try again</button></> : <p role="status" className="text-sm text-mut">Loading account setup…</p>}
      </div> : status.needsConsent ? <div>
        <div className="flex items-center gap-2 mb-1"><Lock className="w-5 h-5 text-navy" /><h1 className="text-xl font-extrabold text-navy">Complete your account setup</h1></div>
        <p className="text-sm text-mut mb-4">These acknowledgements weren't recorded for your account. Review and accept them once to continue.</p>
        <OnboardingAcknowledgements checks={checks} disabled={busy} onChange={(key, checked) => {
          setChecks(current => ({ ...current, [key]: checked })); setErr(null);
        }} />
        <p className="mt-3 text-xs text-mut"><a href="/legal/terms" target="_blank" rel="noreferrer" className="text-brand-red hover:underline">Terms of Service</a> · <a href="/legal/privacy" target="_blank" rel="noreferrer" className="text-brand-red hover:underline">Privacy Policy</a></p>
        {err && <p role="alert" className="text-sm text-brand-red mt-3">{err}</p>}
        <button type="button" onClick={accept} disabled={!allChecked || busy} className="btn-red w-full mt-5 disabled:opacity-50 disabled:cursor-not-allowed">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}I agree — continue
        </button>
      </div> : <div>
        <div className="-mx-6 -mt-6 bg-navy px-6 py-5 text-center text-white sm:-mx-8 sm:-mt-8">
          <h1 className="text-xl font-extrabold">How did you hear about us?</h1>
          <p className="mt-1 text-sm text-white/90">Choose all that apply. This is optional.</p>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-2.5 sm:grid-cols-3">{DISCOVERY_SOURCES.map(({ key, label, mark }) => {
          const selected = selectedSources.includes(key);
          return <button key={key} type="button" disabled={busy} aria-pressed={selected} onClick={() => {
            setSelectedSources((current) => current.includes(key) ? current.filter((source) => source !== key) : [...current, key]);
            setErr(null);
          }} className={`flex min-h-20 flex-col items-center justify-center gap-1.5 rounded-xl border px-2 py-2.5 text-center text-xs font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy ${selected ? "border-brand-red bg-brand-red/5 text-navy ring-1 ring-brand-red" : "border-line bg-navy-50/40 text-navy hover:border-navy-500"}`}>
            <span aria-hidden="true" className={`grid h-7 w-7 place-items-center rounded-full text-sm font-bold shadow-sm ${selected ? "bg-brand-red text-white" : "bg-white text-navy"}`}>{mark}</span><span>{label}</span>
          </button>;
        })}</div>
        {err && <p role="alert" className="text-sm text-brand-red mt-3">{err}</p>}
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" disabled={busy} onClick={() => void finishDiscovery(true)} className="btn-outline disabled:opacity-50">Skip</button>
          <button type="button" disabled={busy || selectedSources.length === 0} onClick={() => void finishDiscovery(false)} className="btn-red disabled:opacity-50">{busy ? <><Loader2 className="w-4 h-4 animate-spin" />Saving…</> : <>Continue<ChevronRight className="w-4 h-4" /></>}</button>
        </div>
      </div>}
    </div>
  </div>;
}
